/**
 * Frontend catalog mirror for the 12 RaiSE personas + 8 RaiSE backgrounds.
 * Thumbnails live in /public/avatar-assets/. Will be replaced by a backend-
 * sourced catalog (avatar_catalog.py + S3) when Bloc 1 lands.
 */

export type Persona = {
  id: string;
  name: string;
  role: string;
  age: string;
  voice: string;
  pitch: string;
  usage: string;
  langs: string[];
};

export type Background = {
  id: string;
  name: string;
  family: string;
  pitch: string;
  mood: string;
};

export const PERSONA_CATALOG: Persona[] = [
  {
    id: "01_henri",
    name: "Henri",
    role: "CEO / dirigeant",
    age: "50s",
    voice: "Daniel · steady broadcaster",
    pitch: "Posé, calme, autorité naturelle. Diction nette, présence rassurante.",
    usage: "Annonces stratégiques, prises de parole institutionnelles, lancements majeurs.",
    langs: ["FR", "EN", "ES"],
  },
  {
    id: "02_sarah",
    name: "Sarah",
    role: "CTO / experte tech",
    age: "40s",
    voice: "Alice · clear educator",
    pitch: "Claire, structurée, pédagogue. Voix qui explique sans condescendance.",
    usage: "Démos produit, vidéos techniques, formation interne, deep-dives architecture.",
    langs: ["FR", "EN"],
  },
  {
    id: "03_thomas",
    name: "Thomas",
    role: "Commercial / sales lead",
    age: "30s",
    voice: "Liam · energetic",
    pitch: "Énergique, rythmé, convaincant. Pousse à l'action sans pression.",
    usage: "Pitchs commerciaux, outreach, présentations clients, vidéos d'événement.",
    langs: ["FR", "EN", "ES"],
  },
  {
    id: "04_leila",
    name: "Leila",
    role: "Product / UX",
    age: "30s",
    voice: "Jessica · playful bright",
    pitch: "Enjouée, lumineuse, créative. Idéale pour parler de design et d'usage.",
    usage: "Stories produit, walkthrough feature, vidéos UX, contenus design system.",
    langs: ["FR", "EN"],
  },
  {
    id: "05_marc",
    name: "Marc",
    role: "Data / analytics",
    age: "40s",
    voice: "Eric · smooth trustworthy",
    pitch: "Souple, calme, crédible. Pose des chiffres sans les jeter à la figure.",
    usage: "Insights chiffrés, rapports trimestriels, reviews données, BI walkthrough.",
    langs: ["FR", "EN"],
  },
  {
    id: "06_marine",
    name: "Marine",
    role: "Témoignage / client success",
    age: "30s",
    voice: "Bella · professional warm",
    pitch: "Chaleureuse, professionnelle, sincère. Crée de la confiance immédiate.",
    usage: "Témoignages clients, success stories, vidéos de réassurance, prises de parole RH.",
    langs: ["FR", "EN", "ES"],
  },
  {
    id: "07_antoine",
    name: "Antoine",
    role: "Corporate / institutionnel",
    age: "50s",
    voice: "Brian · deep resonant",
    pitch: "Grave, profond, résonant. Voix de référence pour le formel et le solennel.",
    usage: "Communications corporate, ESG, déclarations institutionnelles, vidéos investisseurs.",
    langs: ["FR", "EN"],
  },
  {
    id: "08_priya",
    name: "Priya",
    role: "Analyste / consultante",
    age: "30s",
    voice: "Matilda · knowledgeable",
    pitch: "Structurée, posée, factuelle. Maîtrise du sujet palpable.",
    usage: "Études de marché, panels d'experts, recommandations stratégiques, briefings.",
    langs: ["FR", "EN"],
  },
  {
    id: "09_olivier",
    name: "Olivier",
    role: "Challenger / disruptor",
    age: "40s",
    voice: "Adam · dominant firm",
    pitch: "Ferme, affirmé, qui prend position. À utiliser quand il faut secouer.",
    usage: "Tribunes d'opinion, manifestes, vidéos de positionnement, takes contrarians.",
    langs: ["FR", "EN"],
  },
  {
    id: "10_clara",
    name: "Clara",
    role: "Host / animatrice",
    age: "30s",
    voice: "Laura · enthusiast",
    pitch: "Enthousiaste, accueillante, énergie haute. Donne envie de regarder la suite.",
    usage: "Intros d'événement, animation panel, vidéos de bienvenue, onboarding utilisateurs.",
    langs: ["FR", "EN", "ES"],
  },
  {
    id: "11_jean_pierre",
    name: "Jean-Pierre",
    role: "Vlogueur / mentor",
    age: "60s",
    voice: "Bill · wise mature",
    pitch: "Sage, expérimenté, ton de conversation. Transmet plutôt qu'il n'enseigne.",
    usage: "Carnets de bord, retours d'expérience, mentorat vidéo, contenus long-format.",
    langs: ["FR", "EN"],
  },
  {
    id: "12_amina",
    name: "Amina",
    role: "Modératrice / interview",
    age: "30s",
    voice: "Lily · velvety",
    pitch: "Velouté, élégant, posé. Crée un espace pour que l'autre parle.",
    usage: "Interviews, podcasts vidéo, modération de table ronde, voix off chaleureuse.",
    langs: ["FR", "EN"],
  },
];

export const BG_CATALOG: Background[] = [
  {
    id: "fond1_bureau_vitre",
    name: "Bureau vitré",
    family: "Intérieur · bureau",
    pitch: "Bureau d'angle avec verrière, lumière naturelle douce, palette neutre claire.",
    mood: "Sobre, professionnel, immobilier premium",
  },
  {
    id: "fond2_open_space_tech",
    name: "Open space tech",
    family: "Intérieur · bureau moderne",
    pitch: "Open space avec écrans, plantes, design contemporain.",
    mood: "Tech, vivant, équipe en mouvement",
  },
  {
    id: "fond3_scene_conference",
    name: "Scène de conférence",
    family: "Événementiel · keynote",
    pitch: "Estrade avec écran géant en arrière-plan, éclairage scénique.",
    mood: "Keynote, ambition, prise de parole majeure",
  },
  {
    id: "fond4_couloir_conference",
    name: "Couloir de conférence",
    family: "Événementiel · networking",
    pitch: "Couloir d'événement avec foule floue, sponsors en arrière-plan.",
    mood: "Networking, énergie, side-event",
  },
  {
    id: "fond5_stand_exposition",
    name: "Stand d'exposition",
    family: "Événementiel · salon pro",
    pitch: "Stand corporate avec logos, totem, banderoles. Tout est lisible.",
    mood: "Salon, exposition, B2B pur jus",
  },
  {
    id: "fond6_salle_reunion",
    name: "Salle de réunion",
    family: "Intérieur · meeting",
    pitch: "Salle de board avec table en bois, écrans, vue sur l'extérieur.",
    mood: "Comité de direction, sérieux, décision",
  },
  {
    id: "fond7_lounge_networking",
    name: "Lounge networking",
    family: "Événementiel · convivial",
    pitch: "Espace lounge avec canapés, lumière chaude, ambiance feutrée.",
    mood: "Détendu, after-work, conversation",
  },
  {
    id: "fond8_carrousel_louvre",
    name: "Carrousel du Louvre",
    family: "Extérieur · Paris iconique",
    pitch: "Arche du Carrousel avec perspective sur la pyramide, lumière dorée.",
    mood: "Paris, prestige, dimension internationale",
  },
];

export const PERSONA_IDS = PERSONA_CATALOG.map((p) => p.id);
export const BG_IDS = BG_CATALOG.map((b) => b.id);
